/**
 * Option-tree feature-flag gate — `featureFlags.pdfBoxCompressor` (Compress PDF) and
 * `featureFlags.compressNoGainPolicy` (compress-wav Auto, prod fixes 2026-10-02 FIX-3).
 *
 * WHAT IS GATED, AND BY WHOM. xconfig marks nodes with `whenFeatureFlag: { <flag>: <value> }`
 * (xconfig `options.models.ts`): a choice (GROUP_SELECTION) or a note so marked is shown only
 * while every listed flag has that value. Compress PDF (2026-10-02, owner decision) marks
 * `EXTREME_COMPRESSION`, `HIGH_COMPRESSION` and the level notes that describe the PDFBox engine
 * with `{ pdfBoxCompressor: true }`, and the guidance note that is true for today's Ghostscript
 * levels with `{ pdfBoxCompressor: false }`. Nothing is hardcoded here: a tree with no markers
 * (an xconfig build from before the levels existed) is left exactly as it is, old copy included,
 * so this ships safely ahead of the xconfig change.
 *
 * compress-wav (xconfig `AUDIO_CODEC_WAV`) marks its first choice, Auto (`audioCodec=AUTO`), and
 * the note that describes it with `{ compressNoGainPolicy: true, compressWavAutoDefault: true }`,
 * and today's PCM note with `{ compressNoGainPolicy: false }` and
 * `{ compressNoGainPolicy: true, compressWavAutoDefault: false }`. `compressWavAutoDefault` is the
 * flag ironman reads to treat "PCM 16-bit" as a pick rather than the page default (UX main-fixes
 * recheck UXR1-01): the page offers Auto exactly when the edge delivers a PCM 16-bit pick as picked.
 *
 * FAIL CLOSED. Each verdict is `<flag> === true`; absent (prod's `featureFlags` is `{}`), false,
 * or a failed settings call all count as off.
 *
 * ONLY THE FLAGS IN `OPTION_TREE_FLAGS` ARE WIRED. The callers pass `optionTreeFlags(...)` (the page
 * base's verdicts, or the settings emission in the re-edit modal), so a marker naming any other
 * flag reads that flag as OFF: `{ otherFlag: true }` stays hidden and `{ otherFlag: false }` stays
 * shown. Wiring a new flag means adding it to `OPTION_TREE_FLAGS`, the page base's verdicts and
 * `FileConvertComponent`'s props (QA round 1, QA1-03: compressNoGainPolicy was marked in xconfig
 * but never passed here, so compress-wav never showed Auto).
 *
 * WHEN IT CHANGES. Settings are fetched ONCE per page load (`_app.tsx`, `settingsService
 * .initialize()`), after the build-baked props value, so a flip reaches a visitor on their next
 * page load; an open tab keeps the levels it was given. That makes the backend half load-bearing:
 * a job can still carry `HIGH_COMPRESSION` / `EXTREME_COMPRESSION` after the flag goes off (an open
 * tab, a stored job replayed by `services/workflow-recreate.service.ts`, a retry, a public-API
 * call), and ironman must map them to its Screen level when the flag is off, never fail.
 * The same holds for `audioCodec=AUTO`: with compressNoGainPolicy off ironman reads it as its WAV
 * default (PCM 16-bit).
 *
 * HOW. A gated choice goes on the group's context-restore hide list
 * (`GroupViewState.activeHiddenOptionIds`), which `SelectableOptionViewState.isHidden` and
 * `GroupViewState.summarize` already honour: it is not rendered, and a selection on it moves to
 * the group's DEFAULT (`SCREEN`), or its first visible choice when the default itself is the gated
 * one (compress-wav: Auto -> PCM 16-bit), so it is never posted from this page. When a later flag
 * value shows that choice again and the group still sits on the fallback, the choice the gate
 * displaced is selected again: the page gates before its first render with every flag off, and
 * the settings answer arrives after it, so without this compress-wav would keep PCM 16-bit as the
 * selection with Auto on (a user who picked another choice in between keeps it; one who picked
 * the fallback itself in that window is indistinguishable from the fallback). That list is only
 * rewritten by `ensureContextState` for groups that declare `restoreStateContexts`, so a marked
 * choice in such a group cannot be gated: xconfig rejects that at build time
 * (core/utils/feature-flag-markers.ts), and if one ever slips through, this hides the choice for
 * good (an always-true `hidden` clause, which context switches do not touch) and logs once — it
 * never throws, because it runs in FileConvertComponent's constructor. Notes are filtered from a
 * stash of the xconfig originals, so nothing is re-parsed.
 *
 * This module imports nothing but types: it is reached from `file-convert.component.tsx`, which
 * is in the static entry graph.
 */
import type { ConvertOptionsContextV2 } from './convert-options.context';
import type { GroupViewState, Note, ViewState } from './convert-options.view-states';

export const PDF_COMPRESSION_TYPE_GROUP_ID = 'DOCUMENT_COMPRESSION_TYPE';

export type FlagValues = { [flagName: string]: boolean };
type FlagMarker = { whenFeatureFlag?: FlagValues };
type MarkedNote = Note & FlagMarker;

/** Every site flag an option-tree marker may name; anything else reads as off. */
export const OPTION_TREE_FLAGS = ['pdfBoxCompressor', 'compressNoGainPolicy', 'compressWavAutoDefault'] as const;

/** The verdict: the flag reads exactly `true`. Anything else keeps today's page. */
export function pdfCompressPresetsEnabled(flags: { pdfBoxCompressor?: unknown } | null | undefined): boolean {
    return flags?.pdfBoxCompressor === true;
}

/** compress-wav's no-gain policy (FIX-3): `featureFlags.compressNoGainPolicy === true`, fail closed. */
export function compressNoGainPolicyEnabled(flags: object | null | undefined): boolean {
    return optionTreeFlags(flags).compressNoGainPolicy;
}

/** UXR1-01: `featureFlags.compressWavAutoDefault === true`, fail closed. Auto shows only with this AND the policy on. */
export function compressWavAutoDefaultEnabled(flags: object | null | undefined): boolean {
    return optionTreeFlags(flags).compressWavAutoDefault;
}

/** The gate's input from a settings `featureFlags` object: each wired flag, `true` only on an exact `true`. */
export function optionTreeFlags(flags: object | null | undefined): FlagValues {
    const values: FlagValues = {};
    OPTION_TREE_FLAGS.forEach(name => { values[name] = (flags as Record<string, unknown> | null | undefined)?.[name] === true; });
    return values;
}

/** Does a marker allow this node under these flags? An absent marker always does. */
export function flagsAllow(marker: FlagValues | undefined, flags: FlagValues): boolean {
    if (!marker) return true;
    return Object.keys(marker).every(name => (flags[name] === true) === (marker[name] === true));
}

const originalNotes = new WeakMap<object, MarkedNote[] | undefined>();
/** `$nin` over an empty list is always true: "hidden, whatever the results say". */
const ALWAYS_HIDDEN = { $nin: { propertyName: '__xc_feature_flag_gate__', propertyValue: [] as string[] } };
const reportedMisconfig = new Set<string>();

function hideForGood(node: any, group: GroupViewState) {
    // REPLACES the clauses: `hidden` entries are ANDed, so appending would only narrow them.
    node.hidden = [ALWAYS_HIDDEN];
    group.activeHiddenOptionIds.add(node.id);
    const key = `${group.id}/${node.id}`;
    if (!reportedMisconfig.has(key)) {
        reportedMisconfig.add(key);
        // eslint-disable-next-line no-console
        console.warn(`[feature-flag gate] ${node.id} is flag-gated inside ${group.id}, which has restoreStateContexts; hidden for good.`);
    }
}
const appliedFlags = new WeakMap<object, string>();
/** Per tree: the flag values last applied, for `applyPdfCompressPresetGate` (it changes only its own flag). */
const lastFlags = new WeakMap<object, FlagValues>();

/** One key per behaviour: an absent wired flag and a false one gate the same way. */
function flagsKey(flags: FlagValues): string {
    const names = Array.from(new Set([...OPTION_TREE_FLAGS, ...Object.keys(flags)])).sort();
    return JSON.stringify(names.map(name => [name, flags[name] === true]));
}
/** Per group: the choice that was selected when the gate hid it (see HOW above). */
const displacedSelection = new WeakMap<object, string>();

/**
 * Where `GroupViewState.summarize` sends a selection that sits on [node] while it is hidden: the
 * group's default when that is another visible choice, else the first visible choice other than it.
 */
function fallbackFor(group: GroupViewState, node: any, results: any) {
    const visible = (opt: any) => opt !== node && !opt.isHidden(results);
    const defaultOption = group.options.find(opt => opt.id === group.defaultOptionId);
    return defaultOption && visible(defaultOption) ? defaultOption : group.options.find(visible);
}

function walk(node: any, visit: (node: any, parent: any) => void, parent: any = null) {
    if (!node) return;
    visit(node, parent);
    (node.options ?? []).forEach((child: ViewState) => walk(child, visit, node));
}

/**
 * Apply `flags` to every marked node of THIS context. Idempotent: returns true only when these
 * flags were not already the applied ones, so a caller re-renders / emits only on a real change. A tree with no markers is
 * untouched and returns false.
 */
export function applyFeatureFlagGate(context: ConvertOptionsContextV2 | null | undefined, flags: FlagValues): boolean {
    const root = context?.getModel?.();
    if (!root) return false;
    const key = flagsKey(flags);
    lastFlags.set(root, { ...flags });
    if (appliedFlags.get(root) === key) return false;

    let marked = false;
    let results: any;
    const resultsNow = () => {
        if (results === undefined) results = root.getResultForView?.() ?? {};
        return results;
    };
    walk(root, (node, parent) => {
        const notes = originalNotes.has(node) ? originalNotes.get(node) : node.notes as MarkedNote[] | undefined;
        if (notes?.some(n => n?.whenFeatureFlag)) {
            marked = true;
            if (!originalNotes.has(node)) originalNotes.set(node, notes);
            node.notes = notes.filter(n => flagsAllow(n.whenFeatureFlag, flags));
        }
        const marker = (node as FlagMarker).whenFeatureFlag;
        if (marker && parent?.activeHiddenOptionIds instanceof Set) {
            const group = parent as GroupViewState;
            marked = true;
            if (group.restoreStateContexts?.length) {
                // Their context switches clear `activeHiddenOptionIds`, which would un-hide this.
                hideForGood(node, group);
                return;
            }
            if (flagsAllow(marker, flags)) {
                const wasHidden = group.activeHiddenOptionIds.delete(node.id);
                if (wasHidden && displacedSelection.get(group) === node.id) {
                    displacedSelection.delete(group);
                    if (group.selected !== node && group.selected === fallbackFor(group, node, resultsNow())) {
                        group.updateValue(node);
                    }
                }
            } else {
                if (!group.activeHiddenOptionIds.has(node.id) && group.selected === node) {
                    displacedSelection.set(group, node.id);
                }
                group.activeHiddenOptionIds.add(node.id);
            }
        }
    });
    if (!marked) return false;
    appliedFlags.set(root, key);
    return true;
}

/**
 * Compress PDF's flag alone: the other wired flags keep the values last applied to this tree (off on a
 * fresh one), so a caller that only knows about pdfBoxCompressor (FIX-2's "Upload it again" re-gate) cannot
 * hide compress-wav's Auto. The page and the modal pass every wired flag through `applyFeatureFlagGate`.
 */
export function applyPdfCompressPresetGate(context: ConvertOptionsContextV2 | null | undefined, enabled: boolean): boolean {
    const root = context?.getModel?.();
    const last = root ? lastFlags.get(root) : undefined;
    return applyFeatureFlagGate(context, { ...optionTreeFlags(last), pdfBoxCompressor: enabled });
}
